import { useEffect, useState, type ReactNode } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { accessLevelsApi } from '../../../administration/access-levels/accessLevelsApi';
import type { AccessLevel } from '../../../administration/access-levels/accessLevelTypes';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Stepper, type StepDescriptor } from '../../../../components/Stepper';
import { HelpTip } from '../../../../components/HelpTip';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { DateField } from '../../../../components/inputs/DateField';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { PhotoField, type PhotoValue } from '../../../../components/inputs/PhotoField';
import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { Select } from '../../../../components/inputs/Select';
import { PreviewTable } from '../../../../components/PreviewTable';
import { TextField } from '../../../../components/inputs/TextField';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
/*
  A hire date and a date of birth are CALENDAR dates, not instants: put one through the
  instant path and a reader west of UTC is told the day before the one on the form.
*/
import { focusValidationError } from '../../../../lib/validationFocus';
import { identificationApi } from '../../identification/identificationApi';
import type { IdentificationType } from '../../identification/identificationTypes';
import { plantillaApi } from '../../plantilla/plantillaApi';
import { personnelApi, personsApi } from '../personnelApi';
import { usePersonPhoto } from '../usePersonPhoto';
import {
  ADDRESS_KIND,
  CIVIL_STATUS_LABELS,
  EMPLOYMENT_TYPE_LABELS,
  MILITARY_BRANCH_LABELS,
  MILITARY_SERVICE_STATUS_LABELS,
  SEX_LABELS,
  type Citizenship,
  type CivilStatus,
  type DualCitizenshipKind,
  type EmploymentType,
  type MilitaryBranch,
  type MilitaryServiceStatus,
  type PersonnelCatalogs,
  type AccountMode,
  type LinkableAccount,
  type PersonnelAddressInput,
  type PlantillaVacantItem,
  type Sex,
  type UserCredentialIssued,
} from '../personnelTypes';
import { AddressPicker } from '../../../../components/inputs/AddressPicker';
import { CitizenshipFields } from './sections/CitizenshipFields';
import { NO_CATALOGS, samplePersonnel } from '../utils/samplePersonnel';
import { vacantPositionOptions, vacantSlotOptions } from '../utils/vacantPositionOptions';

/**
 * The picker's value for an unclassified record.
 *
 * A sentinel rather than an empty string, because "no level" is a real answer here and not a
 * field the reader has yet to fill in — the same reason the picker is not clearable.
 */
const UNCLASSIFIED = 'unclassified';

type StepKey =
  'personal' | 'address' | 'identification' | 'military' | 'employment' | 'account';

interface PersonnelStep extends StepDescriptor<StepKey> {
  valid: boolean;
  content: ReactNode;
}


/**
 * Create or edit one employee.
 *
 * **Editing loads the identity separately, and that is the split showing through the UI.**
 * The list row carries a `personId` and no name, so a form that edits the name has to ask the
 * identity service for it — one record, `personnel.reveal`, audited. A reader without that key
 * cannot edit an employee's name, which is the correct outcome rather than a gap.
 *
 * **The photograph is written after the record exists**, because it is keyed by `PersonId` and
 * the create response is the first thing to carry one. A failed image upload therefore leaves a
 * saved record: the modal remembers what it created, so a retry re-uploads the photograph
 * rather than creating a second employee.
 */
export function PersonnelModal({
  open,
  editingId,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editingId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [step, setStep] = useState(0);

  /*
    Adding a provider writes a row every future record can draw on, so it is its own key. The
    control follows the key rather than the request: a reader without it gets a plain picker
    of what exists, not a box that invites them to type a value the server will refuse.
  */

  /*
    The Account step offers only the modes the caller can actually complete. Someone without
    `users.create` sees link and skip; someone without `users.read` sees create and skip; a
    reader with neither still gets a record, because skipping is always available.
  */
  const canCreateAccount = usePermission('users.create');
  const canLinkAccount = usePermission('users.read');

  /**
   * Another module's key, because authorizing a seat is the plantilla's write rather than
   * this form's. `manage-slots` and not `update`: changing a headcount is a different
   * decision from fixing a title, which is the line that endpoint already draws.
   */
  const canAddSlot = usePermission('plantilla.manage-slots');

  /*
    CLASSIFYING ON THE WAY IN is its own key, so the field is simply not drawn without it —
    hide, don't disable. The request then carries no level and the record is created
    unclassified, which is what the server would have refused the request for anyway.

    It is on the create only. A PUT rewrites every column it carries, so a level on the edit
    path would be sent back by every reader who corrects anything, and a reader holding
    `personnel.update` and not this key would have ordinary edits refused. Reclassifying is
    the profile's own Access level card.
  */
  const canSetAccessLevel = usePermission('personnel.set-access-level');

  // ── personal ──────────────────────────────────────────────────────────────
  const [photo, setPhoto] = useState<PhotoValue | null>(null);
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [sex, setSex] = useState<Sex>(1);
  const [bloodTypeId, setBloodTypeId] = useState<string | null>(null);
  const [civilStatus, setCivilStatus] = useState<CivilStatus | null>(null);
  const [citizenship, setCitizenship] = useState<Citizenship | null>(null);
  const [dualKind, setDualKind] = useState<DualCitizenshipKind | null>(null);
  const [dualCountryId, setDualCountryId] = useState<string | null>(null);

  // ── address ───────────────────────────────────────────────────────────────
  const [presentAddress, setPresentAddress] = useState<AddressParts>(BLANK_ADDRESS);
  const [permanentAddress, setPermanentAddress] = useState<AddressParts>(BLANK_ADDRESS);
  const [sameAsPresent, setSameAsPresent] = useState(true);
  const [nationalityId, setNationalityId] = useState<string | null>(null);
  const [religionId, setReligionId] = useState<string | null>(null);
  const [isMilitary, setIsMilitary] = useState(false);
  const [isPwd, setIsPwd] = useState(false);
  const [pwdIdNumber, setPwdIdNumber] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');

  // ── identification ────────────────────────────────────────────────────────
  /*
    The identification fields are not fixed any more: the step renders one per ACTIVE type
    in the catalog, so the state is a map keyed by type id rather than five named strings.
    Adding a type adds a field with no change here.
  */
  const [identificationTypes, setIdentificationTypes] = useState<IdentificationType[]>([]);
  const [identifications, setIdentifications] = useState<Record<string, string>>({});

  // ── military ──────────────────────────────────────────────────────────────
  const [militaryBranch, setMilitaryBranch] = useState<MilitaryBranch | null>(null);
  const [militaryServiceStatus, setMilitaryServiceStatus] = useState<MilitaryServiceStatus | null>(
    null,
  );
  const [militaryRank, setMilitaryRank] = useState('');
  const [militaryServiceNumber, setMilitaryServiceNumber] = useState('');
  const [militaryUnit, setMilitaryUnit] = useState('');
  const [militaryEntered, setMilitaryEntered] = useState('');
  const [militarySeparated, setMilitarySeparated] = useState('');

  // ── employment ────────────────────────────────────────────────────────────
  const [dateHired, setDateHired] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType | null>(null);
  /*
    A POSITION, not a slot. The picker lists positions with a count of what is open under
    each — three seats on one job is one row, not three — and the appointment resolves to the
    lowest-numbered open slot when it is written, because an item number is what gets quoted.
  */
  /*
    CARRIED, never asked for. This form stopped collecting the HMO fields on 15 Sep 2026 —
    they are their own profile panel now — but it can still be opened over an existing
    record, and the update endpoint rewrites every column it is sent. Omitting them would
    clear a card number nobody touched, so they are seeded from the record and posted back
    unchanged. On a create they are null, which is the truth: the form did not ask.
  */
  const [hmoProviderId, setHmoProviderId] = useState<string | null>(null);
  const [hmoCardNumber, setHmoCardNumber] = useState('');

  const [plantillaPositionId, setPlantillaPositionId] = useState<string | null>(null);

  /*
    The SLOT, chosen by the reader. The request carries this and not the position: the
    position picker is a narrowing device for the list below it, and the row is written
    against an item. Cleared whenever the position changes, or a reader who switches
    positions keeps a slot belonging to the old one.
  */
  const [plantillaItemId, setPlantillaItemId] = useState<string | null>(null);

  /*
    Authorizing one more seat on the chosen position, from inside the step that came up with
    nothing to pick.

    **The reader does not name it.** An item number is `{Code}-{NNN}`, assigned by the server
    off a sequence that counts past every slot the position has ever had — deleted ones
    included, because the index on it is unfiltered and a number means one seat forever. So
    this asks for no value at all: it adds a seat and selects whatever the plantilla called it.
  */
  const [addingSlot, setAddingSlot] = useState(false);

  async function addSlot() {
    if (plantillaPositionId === null || addingSlot) return;

    setAddingSlot(true);

    try {
      /*
        THE RESPONSE IS THE WHOLE SLOT LIST, so the new seat is the HIGHEST-numbered row in it —
        never `[0]`, which is the post's first slot and may be filled by somebody.

        Found by pressing the control and reading the panel: the picker listed `…-001` twice,
        both ticked, because the row appended below carried slot 001's own id. Nothing in the
        types could have said so — the endpoint returns the same shape either way.
      */
      const slots = await plantillaApi.addSlots(plantillaPositionId, 1);

      const created = slots.reduce<(typeof slots)[number] | null>(
        (newest, slot) =>
          newest === null || slot.sequenceNumber > newest.sequenceNumber ? slot : newest,
        null,
      );

      if (!created) return;

      /*
        Appended to the LOADED list rather than refetching it: a refetch would also drop any
        slot somebody else filled in the meantime, and the reader would watch their own
        selection disappear. The shape is the vacant feed's, built from the position already
        in hand — a seat created a moment ago is vacant by construction.
      */
      const sibling = vacantItems.find((i) => i.plantillaPositionId === plantillaPositionId);

      setVacantItems((current) => [
        ...current,
        {
          id: created.id,
          plantillaPositionId: created.plantillaPositionId,
          itemNumber: created.itemNumber,
          positionTitle: sibling?.positionTitle ?? '',
          orgUnitName: sibling?.orgUnitName ?? '',
          salaryGrade: sibling?.salaryGrade ?? null,
        },
      ]);

      setPlantillaItemId(created.id);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('Could not add a slot to the plantilla.', 0),
      );

      // No focus call: the failure is another module's write and names no field on this form.
    } finally {
      setAddingSlot(false);
    }
  }
  const [vacantItems, setVacantItems] = useState<PlantillaVacantItem[]>([]);

  /*
    The appointment behind the employee's FIRST line of service.

    All three are optional, and they are collected HERE rather than left to the Service Record
    panel because there is no edit for a line of service: a fact not collected on this step is
    one the opening line of the 201 file never carries.
  */
  const [appointmentDate, setAppointmentDate] = useState('');
  const [assumptionDate, setAssumptionDate] = useState('');
  const [appointmentNatureId, setAppointmentNatureId] = useState<string | null>(null);

  /*
    The provider is held as TEXT, not an id, because the picker lets the reader type one that
    is not in the list yet. It resolves to a row on save — created if it is genuinely new,
    matched if someone added it a moment ago.
  */

  // ── account ───────────────────────────────────────────────────────────────
  const [accountMode, setAccountMode] = useState<AccountMode>('skip');
  const [linkUserId, setLinkUserId] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState('');
  /*
    The FIRST name only, derived during render and never stored. Product call, 12 Sep 2026.

    An alias is a handle, not an identity: a first name identifies the account to its holder
    and the people who work with them, and it narrows what a  leak reveals compared with
    a full name. It is still a copy of a name in the main database, which the satellite design
    otherwise forbids — recorded as a deviation rather than left to be discovered.
  */
  const accountAlias = firstName.trim();
  const [linkableAccounts, setLinkableAccounts] = useState<LinkableAccount[]>([]);

  /*
    The issued credential, held so the modal can show it ONCE. It is never persisted in clear
    text and never readable again, so the form stays open on it rather than closing and
    leaving the reader with an account nobody can sign into.
  */
  const [issued, setIssued] = useState<UserCredentialIssued | null>(null);

  const [catalogs, setCatalogs] = useState<PersonnelCatalogs>(NO_CATALOGS);
  const [loading, setLoading] = useState(editingId !== null);

  /*
    The identity store's key for the record being edited, held because the photograph is
    fetched by it and `editingId` is the employment record's id.
  */
  const [loadedPersonId, setLoadedPersonId] = useState<string | null>(null);

  // Whatever the record already has, shown until the reader chooses a replacement.
  const existingPhoto = usePersonPhoto(loadedPersonId);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    The record this modal created, held so a failed photograph upload does not cost a second
    employee on retry: the create already committed, and only the image write is left to redo.
  */
  const [createdPersonId, setCreatedPersonId] = useState<string | null>(null);

  /** The rungs on offer, and the one chosen. Empty and null for a reader without the key. */
  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [accessLevelId, setAccessLevelId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setStep(0);
    setCreatedPersonId(null);

    const controller = new AbortController();

    personnelApi
      .catalogs(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setCatalogs(next);
      })
      .catch(() => {
        // A failed catalog load names no field and must not be reported as one: the pickers
        // render empty and everything else on the form still saves.
      });

    identificationApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setIdentificationTypes(next);
      })
      .catch(() => {
        // The step renders its own empty state; a failed catalog load is not a field error.
      });

    /*
      The clearance ladder, for the Personal step's level picker. Only fetched for a reader who
      may set one — the field is not drawn otherwise, so asking would be a request for a list
      nothing on screen is going to show.
    */
    if (canSetAccessLevel && editingId === null) {
      accessLevelsApi
        .options(controller.signal)
        .then((next) => {
          if (!controller.signal.aborted) setAccessLevels(next);
        })
        .catch(() => {
          // The picker renders empty and the record is created unclassified; a failed catalog
          // load names no field and must not be reported as one.
        });
    }

    personnelApi
      .vacantItems(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setVacantItems(next);
      })
      .catch(() => {
        // Same: a reader without `plantilla.read` still creates records, they just cannot
        // appoint from here — which is what the empty state on that picker says.
      });

    if (canLinkAccount) {
      personnelApi
        .linkableAccounts(controller.signal)
        .then((next) => {
          if (!controller.signal.aborted) setLinkableAccounts(next);
        })
        .catch(() => {
          // The link mode renders its own empty state; a failed feed is not a field error.
        });
    }

    return () => controller.abort();
    /*
      The permissions are dependencies, not incidental reads. They arrive from `/auth/me` after
      mount, so an effect keyed on `open` alone would decide "this reader cannot link an account"
      against a session that had not resolved yet — and never ask again. The picker would sit
      empty for exactly the readers entitled to use it.
    */
  }, [open, canLinkAccount, canCreateAccount, canSetAccessLevel, editingId]);

  useEffect(() => {
    if (editingId === null) return;

    const controller = new AbortController();
    setLoading(true);

    /*
      Two systems, two calls, composed here rather than on the server. The employment record
      comes from `rsys_`; the name comes from the identity store. A single endpoint returning
      both would have to read across the boundary, which no login can do.
    */
    personnelApi
      .get(editingId, controller.signal)
      .then(async (record) => {
        const identity = await personsApi.reveal(record.personId, controller.signal);

        if (controller.signal.aborted) return;

        setLoadedPersonId(record.personId);

        setSalutation(identity.salutation ?? '');
        setFirstName(identity.firstName);
        setMiddleName(identity.middleName ?? '');
        setLastName(identity.lastName);
        setSuffix(identity.suffix ?? '');
        setDateOfBirth(identity.dateOfBirth ?? '');
        setSex(record.sex);
        setCivilStatus(record.civilStatus ?? null);
        setEmploymentType(record.employmentType ?? null);
        setHmoProviderId(record.hmoProviderId ?? null);
        setHmoCardNumber(record.hmoCardNumber ?? '');
        setContactNumber(record.contactNumber ?? '');
        setEmail(record.email ?? '');
        setDateHired(record.dateHired ?? '');
        setBloodTypeId(record.bloodTypeId ?? null);
        setNationalityId(record.nationalityId ?? null);
        setReligionId(record.religionId ?? null);

        // The combobox holds text, so an edit seeds it with the resolved NAME — seeding the id
        // would leave the control blank on a record that has a provider.
        /*
          NOT SEEDED WITH THE NUMBERS, because the record no longer carries them — they moved to
          rsys_pii on 17 Sep 2026 and reading one is an audited reveal.

          Seeded EMPTY, and the submit sends null for anything the reader did not type, which
          means "leave it alone". Seeding blanks and sending empty strings would remove every
          number on save, which is the quiet failure this shape exists to avoid.
        */
        setIdentifications({});
        setIsMilitary(record.isMilitary);
        setMilitaryBranch(record.militaryBranch ?? null);
        setMilitaryServiceStatus(record.militaryServiceStatus ?? null);
        setMilitaryRank(record.militaryRank ?? '');
        setMilitaryServiceNumber(record.militaryServiceNumber ?? '');
        setMilitaryUnit(record.militaryUnit ?? '');
        setMilitaryEntered(record.militaryDateEnteredService ?? '');
        setMilitarySeparated(record.militaryDateSeparated ?? '');
        setIsPwd(record.isPwd);
        setPwdIdNumber(record.pwdIdNumber ?? '');
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load this record.', 0),
        );

        // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [editingId]);

  const personalValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    dateOfBirth.length > 0 &&
    contactNumber.trim().length > 0;

  const militaryValid = !isMilitary || militaryBranch !== null;
  /*
    The position and its slot are REQUIRED. Product call, 15 Sep 2026, reversing the call of
    11 Sep that let a record save without one. The cost is real and stated on the step: with
    no vacancy anywhere in the plantilla, no personnel record can be created at all.
  */
  /*
    Checked here so the reader is told before submitting, and on the server so it holds for any
    caller. Nobody assumes a post before it is given to them.
  */
  const assumedBeforeAppointed =
    appointmentDate !== '' && assumptionDate !== '' && assumptionDate < appointmentDate;

  const employmentValid =
    dateHired.length > 0 && plantillaItemId !== null && !assumedBeforeAppointed;

  /*
    Skipping is always valid — a record without an account is the ordinary case, not a
    concession. Linking needs an account chosen; creating needs the three fields the server
    requires, and nothing more: the password is the server's to issue.
  */
  const accountValid =
    accountMode === 'skip' ||
    (accountMode === 'link' && linkUserId !== null) ||
    (accountMode === 'create' && accountEmail.trim().length > 0 && accountAlias.trim().length > 0);

  const asOptions = (rows: PersonnelCatalogs['bloodTypes']) =>
    rows.map((row) => ({ value: row.id, label: row.name }));

  const steps: PersonnelStep[] = [
    {
      key: 'personal',
      label: 'Personal',
      valid: personalValid,
      content: (
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <FieldGroup
              label="First name"
              labelFor="personnel-first-name"
              description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
              segments={[
                {
                  id: 'personnel-salutation',
                  label: 'Salutation',
                  value: salutation,
                  onChange: setSalutation,
                  options: SALUTATION_OPTIONS,
                  // The empty segment reads "Title" (SALUTATION_EMPTY_LABEL) — "Salutation" does not fit five
                  // characters. Never an em dash: a glyph the form does not use anywhere else.
                  error: error?.fieldError('salutation'),
                  disabled: saving,
                  // The addon gives way, never the grid — a name row is three equal columns.
                  width: SALUTATION_SEGMENT_WIDTH,
                  emptyLabel: SALUTATION_EMPTY_LABEL,
                },
                {
                  id: 'personnel-first-name',
                  label: 'First name',
                  value: firstName,
                  onChange: setFirstName,
                  required: true,
                  error: firstName.trim() ? error?.fieldError('firstName') : 'Enter a first name.',
                  disabled: saving,
                },
              ]}
            />

            <TextField
              id="personnel-middle-name"
              label="Middle name"
              description="Optional — plenty of records have none."
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
              error={error?.fieldError('middleName')}
              disabled={saving}
            />

            <FieldGroup
              label="Last name"
              description="The narrow box is the suffix — Jr., Sr., III. It is part of the name, not a title."
              segments={[
                {
                  id: 'personnel-last-name',
                  label: 'Last name',
                  value: lastName,
                  onChange: setLastName,
                  required: true,
                  error: lastName.trim() ? error?.fieldError('lastName') : 'Enter a last name.',
                  disabled: saving,
                },
                {
                  id: 'personnel-suffix',
                  label: 'Suffix',
                  value: suffix,
                  onChange: setSuffix,
                  error: error?.fieldError('suffix'),
                  disabled: saving,
                  width: 'w-20 shrink-0',
                },
              ]}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="personnel-date-of-birth"
              label="Date of birth"
              required
              showAge
              max={todayCalendarDate()}
              value={dateOfBirth}
              onChange={setDateOfBirth}
              error={dateOfBirth ? error?.fieldError('dateOfBirth') : 'Choose a date of birth.'}
              disabled={saving}
            />

            <Select
              id="personnel-sex"
              label="Sex"
              required
              description="As recorded on the civil registry document."
              value={String(sex)}
              onChange={(next) => setSex(Number(next) as Sex)}
              options={Object.entries(SEX_LABELS).map(([value, label]) => ({ value, label }))}
              sort="none"
              clearable={false}
              disabled={saving}
            />

            <Select
              id="personnel-blood-type"
              label="Blood type"
              value={bloodTypeId}
              onChange={setBloodTypeId}
              options={asOptions(catalogs.bloodTypes)}
              sort="none"
              disabled={saving}
            />

            <Select
              id="personnel-civil-status"
              label="Civil status"
              value={civilStatus === null ? null : String(civilStatus)}
              onChange={(next) =>
                setCivilStatus(next === null ? null : (Number(next) as CivilStatus))
              }
              options={Object.entries(CIVIL_STATUS_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              sort="none"
              disabled={saving}
            />

            <Select
              id="personnel-nationality"
              label="Nationality"
              value={nationalityId}
              onChange={setNationalityId}
              options={asOptions(catalogs.nationalities)}
              sort="none"
              disabled={saving}
            />

            <Select
              id="personnel-religion"
              label="Religion"
              value={religionId}
              onChange={setReligionId}
              options={asOptions(catalogs.religions)}
              sort="none"
              disabled={saving}
            />
          </div>

          {/*
            The same fields the Personal editor shows, from the same component — a new hire
            states their citizenship on the form that records them rather than on a later
            correction, and the two forms cannot drift into asking it differently.
          */}
          <CitizenshipFields
            citizenship={citizenship}
            setCitizenship={setCitizenship}
            kind={dualKind}
            setKind={setDualKind}
            countryId={dualCountryId}
            setCountryId={setDualCountryId}
            countries={catalogs.countries}
            countryName={null}
            saving={saving}
            error={error}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="personnel-contact-number"
              label="Contact number"
              required
              value={contactNumber}
              onChange={(e) => setContactNumber(e.target.value)}
              error={
                contactNumber.trim()
                  ? error?.fieldError('contactNumber')
                  : 'Enter a contact number.'
              }
              disabled={saving}
            />

            <TextField
              id="personnel-email"
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={error?.fieldError('email')}
              disabled={saving}
            />
          </div>

          {/*
            The two flags sit together because each decides what the rest of the form asks:
            military adds a step, PWD adds a field. A control that changes the shape of the
            form belongs where the reader can see both at once.
          */}
          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <label
              htmlFor="personnel-is-military"
              className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            >
              <Checkbox
                id="personnel-is-military"
                name="personnel-is-military"
                checked={isMilitary}
                onChange={(e) => setIsMilitary(e.target.checked)}
                disabled={saving}
              />
              <span>Has served in the military or the police</span>
            </label>

            <label
              htmlFor="personnel-is-pwd"
              className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            >
              <Checkbox
                id="personnel-is-pwd"
                name="personnel-is-pwd"
                checked={isPwd}
                onChange={(e) => setIsPwd(e.target.checked)}
                disabled={saving}
              />
              <span>Is a person with disability</span>
            </label>

            {isPwd ? (
              <TextField
                id="personnel-pwd-id-number"
                label="PWD ID number"
                description="Optional — a record can be flagged before the card is issued."
                value={pwdIdNumber}
                onChange={(e) => setPwdIdNumber(e.target.value)}
                error={error?.fieldError('pwdIdNumber')}
                disabled={saving}
              />
            ) : null}
          </div>

          {/*
            The photograph, LAST on this step and no longer on a summary — the summary step was
            removed on 15 Sep 2026 and this was the one field on it.

            It is still not the first thing asked. The objection that sent it to the end of the
            form was a camera in front of a reader who had not yet said whose record this is,
            and that objection is answered by sitting BELOW the name rather than by sitting on
            another step.
          */}
          <PhotoField
            id="personnel-photo"
            label="Photograph"
            description="Kept in the identity store with the name and date of birth — never in the main database."
            value={photo}
            /*
              Keyed by `PersonId`, and FETCHED. This read `photoUrl(editingId)`, which was
              wrong twice over: the personnel record's id is not the identity store's key,
              and a bare path on an `<img>` carries no bearer token to an API on its own
              origin. Both failed silently, as a photograph that never appeared.
            */
            existingUrl={photo ? null : existingPhoto.url}
            /*
              This form REPLACES a photograph and cannot remove one — its save path only ever
              PUTs. So the clear button reads "Undo" and returns to the stored image, rather
              than offering a removal that the save would silently not perform. Removing one
              is the profile's own photograph editor, under `personnel.delete`.
            */
            removable={false}
            onChange={setPhoto}
            error={error?.fieldError('bytes') ?? error?.fieldError('contentType')}
            disabled={saving}
          />

          {/*
            WHO MAY OPEN THIS FILE, asked here so a record that should be restricted is never
            readable by everyone between being created and being classified.

            LAST on this step and below the photograph, because it is not a fact about the
            person — it is a decision about the record, and it reads as the thing you settle
            once the subject is on screen.

            Drawn only for a reader holding `personnel.set-access-level`: hide, don't disable.
            Without the key the request carries no level, which is the same record the server
            would have produced anyway.
          */}
          {canSetAccessLevel && editingId === null ? (
            <Select
              id="personnel-access-level-id"
              label="Access level"
              description="The clearance a reader needs to open this record. Leave it unclassified unless this file is sensitive."
              value={accessLevelId ?? UNCLASSIFIED}
              onChange={(next) => setAccessLevelId(next === UNCLASSIFIED || !next ? null : next)}
              disabled={saving}
              // The ladder's own order, strongest first — alphabetically "Level 10" sorts
              // above "Level 2", and the order IS the meaning here.
              sort="none"
              clearable={false}
              options={[
                {
                  value: UNCLASSIFIED,
                  label: 'Unclassified',
                  sublabel: 'Readable by everyone who may read personnel records',
                },
                ...accessLevels.map((level) => ({
                  value: level.id,
                  label: level.name,
                  sublabel: level.description ?? undefined,
                  right: <span className="font-mono tabular-nums">Rank {level.rank}</span>,
                })),
              ]}
              error={error?.fieldError('accessLevelId')}
            />
          ) : null}
        </div>
      ),
    },
    {
      key: 'address',
      label: 'Address',

      /*
        Nothing here is required. A record can exist before anyone has asked where the person
        lives, and a create form that refuses to save without an address turns a hiring into a
        data-collection exercise.
      */
      valid: true,
      content: (
        <div className="flex flex-col gap-4">
          <AddressFields
            legend="Present address"
            idPrefix="personnel-address-present"
            value={presentAddress}
            onChange={setPresentAddress}
            saving={saving}
            error={error}
          />

          <div className="border-t border-slate-200 pt-4">
            <label
              htmlFor="personnel-address-same"
              className="flex cursor-pointer items-center gap-2 text-sm text-slate-700"
            >
              <Checkbox
                id="personnel-address-same"
                name="personnel-address-same"
                checked={sameAsPresent}
                onChange={(e) => setSameAsPresent(e.target.checked)}
                disabled={saving}
              />
              <span>The permanent address is the same as the present address</span>
            </label>
          </div>

          {/*
            Hidden rather than disabled: with the box ticked there is no permanent address to
            collect, and a greyed-out copy of the block is a form asking a question it has
            just been told does not apply.
          */}
          {sameAsPresent ? null : (
            <AddressFields
              legend="Permanent address"
              idPrefix="personnel-address-permanent"
              value={permanentAddress}
              onChange={setPermanentAddress}
              saving={saving}
              error={error}
            />
          )}
        </div>
      ),
    },
    {
      key: 'identification',
      label: 'Identification',
      // Nothing here is required: a record can exist before the numbers are collected.
      valid: true,
      content: (
        <div className="flex flex-col gap-4">
          {identificationTypes.length === 0 ? (
            <p className="text-xs text-slate-500">
              No identification types are set up. Add them under Personnel → Settings →
              Identification, and they appear here.
            </p>
          ) : (
            /*
              A KEY/VALUE TABLE, not a grid of labelled boxes. Product call, 14 Sep 2026.

              This is the one form in the product that takes the Table Display View treatment,
              and the reason is the shape of the data: the step is a dozen rows of the same
              thing — a statutory number against its name — rendered from a catalog, so every
              field is identical except its label. A grid makes the reader scan twelve boxes to
              find one; a table lets them run down the names.

              The label is the LEFT cell, tinted, exactly as every other key/value table in
              the product draws its key — including the profile panel that shows these same
              values back. It shipped mirrored for one revision and was swapped.
            */
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full border-collapse">
                <tbody className="divide-y divide-slate-200">
                  {identificationTypes.map((type) => (
                    <tr key={type.id}>
                      {/*
                        The key cell, and it IS the label — `htmlFor` on the cell's own
                        `<label>`, so clicking the name focuses the box. It does NOT name the
                        control: `aria-label` wins over an associated label, so the announced
                        name is the `label` prop, which is why the two must be the same string.
                      */}
                      <td className="w-[38%] border-r border-slate-200 bg-slate-50 px-4 py-3 align-middle">
                        <div className="flex items-center gap-1.5">
                          <label
                            htmlFor={`personnel-identification-${type.id}`}
                            className="cursor-pointer text-sm text-slate-500"
                          >
                            {type.name}
                          </label>

                          {/* The description keeps its help icon: a line of prose in the cell
                              would make the rows different heights. */}
                          {type.description ? <HelpTip label={type.description} /> : null}
                        </div>
                      </td>
                      {/*
                        The control ALONE, from the shared input — `labelHidden` hands the
                        label to the cell beside it rather than replacing the component with a
                        bare `<input>`, which would lose the error wiring and the visual
                        baseline every other field in this form has.
                      */}
                      <td className="w-[62%] p-2">
                        <TextField
                          labelHidden
                          id={`personnel-identification-${type.id}`}
                          label={type.name}
                          /*
                            Passed even though the visible help icon is drawn in the cell:
                            this is what renders the `sr-only` twin and wires
                            `aria-describedby` to it. Without it the icon would be a
                            description only a sighted reader gets, which is the defect the
                            pairing exists to prevent.
                          */
                          description={type.description ?? undefined}
                          value={identifications[type.id] ?? ''}
                          onChange={(e) =>
                            setIdentifications((current) => ({
                              ...current,
                              [type.id]: e.target.value,
                            }))
                          }
                          disabled={saving}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            NO HMO on this step. Product call, 15 Sep 2026: the provider and the card number
            were collected here and DISPLAYED on the profile’s Employment card, so a field
            entered under Identification was corrected under Employment — the panel-mirror
            rule broken in the one place nobody looks. They are their own panel now, with
            their own editor, and the wizard does not ask for them at all: a card is issued
            after somebody is hired rather than while they are being typed in.
          */}
        </div>
      ),
    },
    ...(isMilitary
      ? [
          {
            key: 'military' as const,
            label: 'Military',
            valid: militaryValid,
            content: (
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    id="personnel-military-branch"
                    label="Branch of service"
                    required
                    value={militaryBranch === null ? null : String(militaryBranch)}
                    onChange={(next) =>
                      setMilitaryBranch(next === null ? null : (Number(next) as MilitaryBranch))
                    }
                    options={Object.entries(MILITARY_BRANCH_LABELS).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                    sort="none"
                    error={militaryBranch === null ? 'Choose a branch of service.' : undefined}
                    disabled={saving}
                  />

                  <Select
                    id="personnel-military-service-status"
                    label="Service status"
                    value={militaryServiceStatus === null ? null : String(militaryServiceStatus)}
                    onChange={(next) =>
                      setMilitaryServiceStatus(
                        next === null ? null : (Number(next) as MilitaryServiceStatus),
                      )
                    }
                    options={Object.entries(MILITARY_SERVICE_STATUS_LABELS).map(
                      ([value, label]) => ({ value, label }),
                    )}
                    sort="none"
                    disabled={saving}
                  />

                  <TextField
                    id="personnel-military-rank"
                    label="Rank"
                    value={militaryRank}
                    onChange={(e) => setMilitaryRank(e.target.value)}
                    error={error?.fieldError('militaryRank')}
                    disabled={saving}
                  />

                  <TextField
                    id="personnel-military-service-number"
                    label="Serial / service number"
                    value={militaryServiceNumber}
                    onChange={(e) => setMilitaryServiceNumber(e.target.value)}
                    error={error?.fieldError('militaryServiceNumber')}
                    disabled={saving}
                  />
                </div>

                {/* The unit is a free-text line and takes the width — a rank shares a row, a
                    unit name does not. */}
                <TextField
                  id="personnel-military-unit"
                  label="Unit / assignment"
                  value={militaryUnit}
                  onChange={(e) => setMilitaryUnit(e.target.value)}
                  error={error?.fieldError('militaryUnit')}
                  disabled={saving}
                />

                {/* The two dates are one fact with two ends, so they read as a pair. */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <DateField
                    id="personnel-military-date-entered-service"
                    label="Date entered service"
                    max={todayCalendarDate()}
                    value={militaryEntered}
                    onChange={setMilitaryEntered}
                    error={error?.fieldError('militaryDateEnteredService')}
                    disabled={saving}
                  />

                  <DateField
                    id="personnel-military-date-separated"
                    label="Date separated"
                    description="Leave empty while still serving."
                    max={todayCalendarDate()}
                    value={militarySeparated}
                    onChange={setMilitarySeparated}
                    error={error?.fieldError('militaryDateSeparated')}
                    disabled={saving}
                  />
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'employment',
      label: 'Employment',
      valid: employmentValid,
      content: (
        <div className="flex flex-col gap-4">
          {/*
            The position leads the step: it is the thing being decided, and the dates below
            describe the employment it sits in.

            ONE picker, unit and position merged, searchable — a reader hiring someone thinks
            "which position are they going into", and asking that as two pickers made them
            hold the staffing pattern in their head.

            It offers only VACANT positions and never creates one. Product call, 11 Sep 2026:
            where the plantilla has nothing for the person being hired, the record still saves
            and the appointment is made later.
          */}
          <Select
            id="personnel-plantilla-position-id"
            label="Position"
            required
            description="Vacant positions only, across every unit."
            value={plantillaPositionId}
            onChange={(next) => {
              setPlantillaPositionId(next);

              // The slot belongs to the position; keeping it across a change would post a
              // seat in a job the reader just moved off.
              setPlantillaItemId(null);
            }}
            options={vacantPositionOptions(vacantItems)}
            sort="none"
            error={error?.fieldError('plantillaPositionId')}
            disabled={saving}
          />

          {/*
            The SLOT, after the position and narrowed to it. Product call, 15 Sep 2026: the
            two were one picker and the server took the lowest-numbered open seat, which is
            the right default and the wrong rule once an employee can hold several posts —
            the reader placing somebody often means a particular item number.

            **Always DRAWN, and disabled until a position is chosen** — the same call, later the
            same day, and it is made in BOTH places or the two forms drift: this step and
            `<ServiceRecordModal>` ask one question with one pair of pickers, and a reader who
            filled the wizard should find the service-record form behaving identically.

            It used to render only once a position was picked, so a required field appeared out
            of nowhere and the dates below it jumped down the step. Disabled for STATE, never
            hidden — hiding is what a permission gate does, and this is not one — and it says
            why through its own `description`, which carries the `sr-only` twin with it.
          */}
          <Select
            id="personnel-plantilla-item-id"
            label="Plantilla slot"
            required
            description={
              plantillaPositionId === null
                ? 'Choose a position first — the slots belong to it.'
                : canAddSlot
                  ? 'The item number this appointment is written against. Add a slot from the list to authorize one more seat on this position — the plantilla assigns the item number.'
                  : 'The item number this appointment is written against.'
            }
            value={plantillaItemId}
            onChange={setPlantillaItemId}
            options={vacantSlotOptions(vacantItems, plantillaPositionId)}
            sort="none"
            /*
              The create row, INSIDE the picker rather than a button beside it — the reader
              looking for a seat that is not in the list is already in the panel, and a second
              control under it asked them to leave it to answer the same question.

              `createLabel` and not the usual `Add "<q>"`: the reader does NOT name a slot. An
              item number is assigned by the plantilla, so a row quoting their query would
              promise text the server throws away — which is why this row is also offered with
              nothing typed. The handler OWNS the value and selects the id it comes back with.

              NOT staged. Every other picker that writes a catalog row holds it until the form
              saves; a slot cannot be, because its item number is the server's and there is
              nothing to carry until it has been asked. It is the plantilla's own write, under
              the plantilla's own key, and it stands whether or not this form is submitted.
            */
            allowCustom={canAddSlot && !addingSlot}
            createLabel="Add a slot"
            onCreateOption={() => addSlot()}
            error={error?.fieldError('plantillaItemId')}
            disabled={saving || plantillaPositionId === null}
          />

          {vacantItems.length === 0 ? (
            <p className="text-xs text-slate-500">
              No vacant positions in the plantilla. A record cannot be created until one
              exists — add a post there first.
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="personnel-date-hired"
              label="Date hired"
              required
              description="When employment began. An appointment made here starts on the same day."
              max={todayCalendarDate()}
              value={dateHired}
              onChange={setDateHired}
              error={dateHired ? error?.fieldError('dateHired') : 'Choose a hire date.'}
              disabled={saving}
            />

            <Select
              id="personnel-employment-type"
              label="Employment"
              /*
                The STATUS of the appointment, and the description says so rather than calling
                it the nature — that is the picker below, and two fields answering to one name
                on one step is the confusion the rename exists to prevent.
              */
              description="How the employee is engaged — not the position it is against."
              value={employmentType === null ? null : String(employmentType)}
              onChange={(next) =>
                setEmploymentType(next === null ? null : (Number(next) as EmploymentType))
              }
              options={Object.entries(EMPLOYMENT_TYPE_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
              sort="none"
              disabled={saving}
            />
          </div>

          {/*
            THREE dates on one line of service, and they are not the same fact: the hire date
            above is when employment began, the appointment is the paper somebody signed, and
            the assumption is the day they reported.

            NEITHER of these is capped at today, unlike the hire date. An instrument dated ahead
            of the assumption is normal, and a guard copied from the field above would refuse a
            paper the reader is holding.
          */}
          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              id="personnel-appointment-date"
              label="Appointment date"
              description="The day the appointment instrument is dated."
              value={appointmentDate}
              onChange={setAppointmentDate}
              error={error?.fieldError('appointmentDate')}
              disabled={saving}
            />

            <DateField
              id="personnel-assumption-date"
              label="Assumption date"
              description="The day the employee assumed the duties of the post."
              value={assumptionDate}
              min={appointmentDate || undefined}
              onChange={setAssumptionDate}
              error={
                assumedBeforeAppointed
                  ? 'Choose a date on or after the appointment date.'
                  : error?.fieldError('assumptionDate')
              }
              disabled={saving}
            />
          </div>

          {/*
            The nature of the appointment — WHY the movement happened, which is a different
            question from the Employment picker above. The catalog travels with every other
            vocabulary on this form, so there is no second fetch and no second loading state.

            No `allowCustom` here, unlike the Service Record form: this step is already five
            pickers deep, and a reader creating an employee is not the reader curating a
            vocabulary. Adding one is the service-record form's affordance and the settings
            page's.
          */}
          <Select
            id="personnel-appointment-nature-id"
            label="Nature of appointment"
            description="Why the movement happened — original appointment, promotion, transfer, reassignment."
            value={appointmentNatureId}
            onChange={(next) => setAppointmentNatureId(next || null)}
            options={catalogs.appointmentNatures.map((n) => ({ value: n.id, label: n.name }))}
            sort="none"
            error={error?.fieldError('appointmentNatureId')}
            disabled={saving}
          />
        </div>
      ),
    },
    {
      key: 'account',
      label: 'Account',
      valid: accountValid,
      content: (
        <div className="flex flex-col gap-4">
          {/*
            Radio CARDS, not a dropdown. Three mutually exclusive answers, each needing a line
            that says what choosing it does — a dropdown hides two of the three until opened and
            has nowhere to put the explanation. The fields each mode needs render directly
            beneath, so the choice and its consequence read as one control.

            Skip leads because it is the ordinary case: most employees never need a sign-in.
          */}
          <RadioGroup
            id="personnel-account-mode"
            label="User account"
            value={accountMode}
            onChange={setAccountMode}
            columns={1}
            disabled={saving}
            options={[
              {
                value: 'skip',
                label: 'No account',
                description:
                  'The record is created without a sign-in. One can be linked or created later from the profile.',
              },
              {
                value: 'link',
                label: 'Link an existing account',
                description: canLinkAccount
                  ? 'Attach an account that has no employee yet.'
                  : 'Requires permission to read accounts.',
                disabled: !canLinkAccount,
              },
              {
                value: 'create',
                label: 'Create an account',
                description: canCreateAccount
                  ? 'The server issues a password and shows it once, here, when the record saves.'
                  : 'Requires permission to create accounts.',
                disabled: !canCreateAccount,
              },
            ]}
          />
          {accountMode === 'link' ? (
            linkableAccounts.length === 0 ? (
              <p className="text-xs text-slate-500">
                Every active account already belongs to an employee. Create one instead, or save
                without an account and link one later.
              </p>
            ) : (
              <Select
                id="personnel-link-user"
                label="Account"
                required
                description="Only accounts with no employee attached are listed."
                value={linkUserId}
                onChange={setLinkUserId}
                options={linkableAccounts.map((account) => ({
                  value: account.id,
                  label: account.alias,
                  right: account.email,
                }))}
                sort="none"
                error={linkUserId ? error?.fieldError('userId') : 'Choose an account to link.'}
                disabled={saving}
              />
            )
          ) : null}

          {accountMode === 'create' ? (
            <div className="flex flex-col gap-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  id="personnel-account-email"
                  label="Username / email"
                  type="email"
                  required
                  description="Used as the sign-in name. Must be unique across all accounts."
                  value={accountEmail}
                  onChange={(e) => setAccountEmail(e.target.value)}
                  error={
                    accountEmail.trim() ? error?.fieldError('email') : 'Enter a username or email.'
                  }
                  disabled={saving}
                />

                {/*
                  LOCKED to the name collected on step one. This account belongs to the person
                  the record is about, so a second, separately-typed label would be a name to
                  keep in step with the first — and the one that drifts is the one nobody
                  notices. Disabled for state, and the help text says where it comes from.
                */}
                <TextField
                  id="personnel-account-alias"
                  label="Alias"
                  description="Taken from the employee's first name. Change it on the Personal step."
                  value={accountAlias}
                  onChange={() => {}}
                  disabled
                />
              </div>

              {/*
                NO role picker. The person filling in an employment record is not the person who
                decides what that employee may do, so the account is created without one and
                appears in User management awaiting a role. It can sign in and reach nothing
                until someone with `users.update` answers that.
              */}
              <p className="text-xs text-slate-500">
                The account is created with no role: it can sign in but reaches nothing until a role
                is assigned from User management. The server generates the password and shows it
                once, on this screen, after the record saves — copy it before closing, because it is
                never stored in readable form.
              </p>
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  const stepIndex = Math.min(step, steps.length - 1);
  const isLastStep = stepIndex === steps.length - 1;
  const allValid = steps.every((s) => s.valid);
  const canSave = allValid && confirmed && !saving && !loading;

  /** Which step a server-named field lives on, so a rejection can move the reader to it. */
  function stepOf(field: string): number {
    const identification = [
      'sssNumber',
      'gsisNumber',
      'tinNumber',
      'philHealthNumber',
      'pagIbigNumber',
    ];

    const address = [
      'streetAddress',
      'barangay',
      'cityMunicipality',
      'province',
      'zipCode',
      'addresses',
    ];

    const key: StepKey = address.includes(field)
      ? 'address'
      : field.startsWith('military')
        ? 'military'
        : identification.includes(field)
          ? 'identification'
          : field === 'dateHired' ||
                field === 'employmentType' ||
                field === 'plantillaPositionId' ||
                field === 'plantillaItemId' ||
                field === 'appointmentDate' ||
                field === 'assumptionDate' ||
                field === 'appointmentNatureId'
              ? 'employment'
              : 'personal';

    const index = steps.findIndex((s) => s.key === key);

    return index < 0 ? 0 : index;
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      salutation: salutation.trim() ? salutation.trim() : null,
      firstName: firstName.trim(),
      middleName: middleName.trim() || null,
      lastName: lastName.trim(),
      suffix: suffix.trim() || null,
      dateOfBirth,
      sex,
      civilStatus,
      citizenship,

      // Cleared unless the answer is dual — the server refuses the contradiction, and a form
      // should not knowingly post something it has been told is wrong.
      dualCitizenshipKind: citizenship === 2 ? dualKind : null,
      dualCitizenshipCountryId: citizenship === 2 ? dualCountryId : null,
      employmentType,

      // Carried through untouched — see the state above.
      hmoProviderId,
      hmoCardNumber: hmoCardNumber.trim() || null,

      contactNumber: contactNumber.trim() || null,
      email: email.trim() || null,
      dateHired: dateHired || null,
      bloodTypeId,
      nationalityId,
      religionId,
      // Every type the form rendered, blanks included — the server drops the empty ones,
      // which is also what clears a number the reader emptied on an edit.
      identifications: identificationTypes.map((type) => ({
        identificationTypeId: type.id,
        number: identifications[type.id]?.trim() ?? '',
      })),
      isMilitary,
      militaryBranch: isMilitary ? militaryBranch : null,
      militaryServiceStatus: isMilitary ? militaryServiceStatus : null,
      militaryRank: isMilitary ? militaryRank.trim() || null : null,
      militaryServiceNumber: isMilitary ? militaryServiceNumber.trim() || null : null,
      militaryUnit: isMilitary ? militaryUnit.trim() || null : null,
      militaryDateEnteredService: isMilitary ? militaryEntered || null : null,
      militaryDateSeparated: isMilitary ? militarySeparated || null : null,
      isPwd,
      pwdIdNumber: isPwd ? pwdIdNumber.trim() || null : null,
      permanentAddressSameAsPresent: sameAsPresent,

      // Null unless this reader holds the key and chose a rung — the field is not drawn
      // otherwise, and the server refuses the whole request for a level it did not grant.
      accessLevelId,

      /*
        Only the addresses actually stated. An empty block is not sent as a row of nulls — a
        record with no address has no address row, which is what the profile reads to decide
        between "not collected" and "collected as blank".
      */
      addresses: [
        ...(hasAddress(presentAddress) ? [{ kind: ADDRESS_KIND.present, ...presentAddress }] : []),
        ...(!sameAsPresent && hasAddress(permanentAddress)
          ? [{ kind: ADDRESS_KIND.permanent, ...permanentAddress }]
          : []),
      ],
      /*
        The POSITION goes on the wire and the server allocates the slot inside the same
        transaction that checks vacancy. Resolving it here would mean choosing from a list
        fetched when the modal opened — stale by the time it is sent, and a rejection would
        name an item number the reader never chose.
      */
      plantillaItemId,

      /*
        The first line of service's own facts. Empty is ABSENT, not a blank date: the server
        distinguishes "not recorded" from a value, and "" would be neither.
      */
      appointmentDate: appointmentDate || null,
      assumptionDate: assumptionDate || null,
      appointmentNatureId,

      // Not asked for any more: the server starts the line of service on the hire date.

      // Exactly one of these is ever set — the server refuses both, because they are two
      // answers to one question.
      userId: accountMode === 'link' ? linkUserId : null,
      account:
        accountMode === 'create'
          ? {
              email: accountEmail.trim(),
              alias: accountAlias,
            }
          : null,
    };

    try {

      /*
        The record first, the photograph second — the same order the server writes its two
        databases in, and for the same reason: an image with no record is recoverable, while a
        record whose image failed is merely missing a picture.
      */
      let personId = createdPersonId;

      if (!personId) {
        if (editingId) {
          personId = (await personnelApi.update(editingId, request)).personId;
        } else {
          const created = await personnelApi.create(request);

          personId = created.personnel.personId;
          setCreatedPersonId(personId);

          // Held for the panel below. This is the only time the password exists outside a
          // hash, so it is kept in state and shown rather than logged or re-fetched.
          if (created.account) setIssued(created.account);
        }
      }

      if (photo) {
        await personsApi.setPhoto(personId, photo);
      }

      /*
        A record with no credential closes as usual. One WITH a credential does not: the
        password is readable exactly once, and closing the form on it would leave an account
        nobody can sign into. The reader dismisses it, and that dismissal is what completes
        the save as far as the list is concerned.
      */
      if (accountMode !== 'create') onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);
      /*
        The photograph is one control standing for the two fields the identity store
        rejects it with — `bytes` and `contentType` — and its id does not follow the
        prefix convention, because it is named for the thing rather than for the payload.
      */
      focusValidationError(failure, {
        idPrefix: 'personnel',
        fieldIds: { bytes: 'personnel-photo', contentType: 'personnel-photo' },
        stepOf,
        onStep: setStep,
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    // The catalogs the form has already loaded, so every foreign key is a row that exists.
    // An invented id would be refused by the server's "must exist and be active" check, and
    // the failure would read as a bug in the form rather than in the generator.
    const sample = samplePersonnel({ catalogs, identificationTypes });

    /*
      The position and its slot became REQUIRED on 15 Sep 2026, so the generator owes both or
      Autogenerate silently stops meeting its contract — the primary action would stay
      disabled and the button would read as broken.

      One real open item out of the live feed, and the position is read off it rather than
      picked separately: choosing them independently can produce a slot that does not belong
      to the position above it, which the server refuses.
    */
    const openItem =
      vacantItems.length > 0
        ? vacantItems[Math.floor(Math.random() * vacantItems.length)]
        : null;

    setPlantillaPositionId(openItem?.plantillaPositionId ?? null);
    setPlantillaItemId(openItem?.id ?? null);

    /*
      The first line of service's own facts, filled so the profile behind this form is not a
      column of em dashes — "Save enables" is the floor rather than the whole contract.

      The instrument is dated BEFORE the assumption, which is the one ordering the server
      refuses to have backwards: a generator producing a rejection is worse than one filling
      less. The nature is a real row out of the catalog already loaded, or nothing when the
      catalog is empty, because the field is optional and an invented id would be refused.
    */
    setAppointmentDate(sample.appointmentDate);
    setAssumptionDate(sample.assumptionDate);
    setAppointmentNatureId(sample.appointmentNatureId);

    setSalutation(sample.salutation);
    setFirstName(sample.firstName);
    setMiddleName(sample.middleName);
    setLastName(sample.lastName);
    setDateOfBirth(sample.dateOfBirth);
    setSex(sample.sex);
    setCivilStatus(sample.civilStatus);
    setCitizenship(sample.citizenship);

    /*
      The generator already carried these; the step that uses them did not exist until now.
      Synthetic places on purpose — a real province is a real address, which the rule forbids
      as plainly as it forbids a real name. They are stored as text, so an invented place is
      accepted where an invented catalog id would be refused.
    */
    setPresentAddress(sample.presentAddress);

    /*
      Filled behind the tick, which STAYS AT ITS DEFAULT — exactly as the PWD id number and
      the military block are filled behind their own flags. A generator that unticks it
      produces a different form on each click, and the permanent block is exercised by
      unticking, which is one click.

      This shipped the other way for a revision: the tick came back unchecked after every
      Autogenerate, which reads as the default being wrong rather than as the generator
      having changed it.
    */
    setPermanentAddress(sample.permanentAddress);
    setEmploymentType(sample.employmentType);
    setContactNumber(sample.contactNumber);
    setEmail(sample.email);
    setDateHired(sample.dateHired);

    /*
      The optional-but-fillable half. Leaving these empty still enabled Save, which is why the
      gap went unnoticed — but it produced a profile of em dashes that exercised none of the
      panels behind the form.
    */
    setBloodTypeId(sample.bloodTypeId);
    setNationalityId(sample.nationalityId);
    setReligionId(sample.religionId);
    setIdentifications(sample.identifications);
    setSuffix(sample.suffix);

    /*
      Filled BEHIND their flags, which stay at their defaults. `isMilitary` adds a whole step
      and `isPwd` reveals a field, so flipping either would make the same button produce a
      different form on each click — but with the values already here, one tick exercises the
      section instead of asking for seven more fields.
    */
    setPwdIdNumber(sample.pwdIdNumber);
    setMilitaryBranch(sample.militaryBranch);
    setMilitaryServiceStatus(sample.militaryServiceStatus);
    setMilitaryRank(sample.militaryRank);
    setMilitaryServiceNumber(sample.militaryServiceNumber);
    setMilitaryUnit(sample.militaryUnit);
    setMilitaryEntered(sample.militaryEntered);
    setMilitarySeparated(sample.militarySeparated);

    /*
      The account is NOT generated. Autogenerate fills required fields so the primary action
      enables, and skipping is already valid — minting a real sign-in with a real password as a
      side effect of a convenience button is not a thing this should do.
    */
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingId ? 'Edit personnel record' : 'New personnel record'}
      // The name row alone needs three columns; the steps below it inherit the width.
      size="2xl"
      id="personnel-modal"
      /* Create AND edit — see "Autogenerate" in CLAUDE.md, reversed 14 Sep 2026. */
      /*
        Disabled with an EMPTY plantilla. The Employment step requires a position and a slot
        from 15 Sep 2026, and there is no honest value to invent for either — a button that
        filled every other field and left the primary action disabled would read as broken,
        which is the one thing Autogenerate exists to prevent.
      */
      autogenerate={{ onGenerate: generate, disabled: saving || vacantItems.length === 0 }}
      /*
        LAST STEP ONLY. The tick certifies the whole transaction, so offering it on step one
        invites confirming a form the reader has not seen the rest of — and the primary action
        it gates is only reachable from here anyway.
      */
      confirm={
        isLastStep && !issued
          ? {
              id: 'personnel-confirm',
              checked: confirmed,
              onChange: setConfirmed,
              disabled: saving || loading,
            }
          : undefined
      }
      footer={
        issued ? (
          <Button
            id="personnel-done"
            variant="primary"
            onClick={() => {
              setIssued(null);
              onSaved();
            }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button id="personnel-cancel" variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>

            {/*
            Back sits next to Next — the pair that moves through the form — and renders from
            the second step on, exactly as Next stops rendering on the last. It is never gated
            on the step being valid: going back is how a reader FIXES an invalid step, so
            gating it would strand them on the one they came to correct.
          */}
            {stepIndex > 0 ? (
              <Button
                id="personnel-back"
                variant="secondary"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={saving}
              >
                Back
              </Button>
            ) : null}

            {!isLastStep ? (
              <Button
                id="personnel-next"
                variant="secondary"
                onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                disabled={!steps[stepIndex].valid}
              >
                Next
              </Button>
            ) : null}

            <Button
              id="personnel-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>

            {createdPersonId ? (
              <p className="mt-1 text-xs text-red-600">
                The record was saved and only the photograph failed. Saving again re-uploads it
                without creating a second employee.
              </p>
            ) : null}
          </div>
        ) : null}

        {issued ? (
          <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-medium text-slate-800">The account is created.</p>

            <p className="text-xs text-slate-600">
              This password is shown once and is not stored in readable form. Copy it now and hand
              it over; if it is lost it has to be reset, not recovered.
            </p>

            <PreviewTable
              variant="table-display"
              rows={[
                {
                  label: 'Sign-in email',
                  value: <span className="font-mono">{issued.email}</span>,
                },
                {
                  label: 'Password',
                  value: <span className="font-mono">{issued.generatedPassword ?? '—'}</span>,
                },
                {
                  label: 'First sign-in',
                  value: issued.mustChangePassword
                    ? 'Must change this password'
                    : 'No change required',
                },
              ]}
            />
          </div>
        ) : loading ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <>
            <Stepper
              id="personnel"
              steps={steps.map(({ key, label }) => ({ key, label }))}
              activeIndex={stepIndex}
              onJump={(index) => {
                // Forward movement requires every earlier step to be valid; back is free.
                if (index <= stepIndex || steps.slice(0, index).every((s) => s.valid)) {
                  setStep(index);
                }
              }}
            />

            <div className="pt-2">{steps[stepIndex].content}</div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** One address, as the create form collects it. `kind` is added when the row is built. */
type AddressParts = Omit<PersonnelAddressInput, 'kind'>;

const BLANK_ADDRESS: AddressParts = {
  streetAddress: null,
  barangay: null,
  cityMunicipality: null,
  province: null,
  zipCode: null,
};

/**
 * Whether anything was actually stated.
 *
 * An untouched block is NOT sent. A row of nulls is a record that has an address consisting of
 * nothing, which reads differently from a record nobody has asked yet — and the profile's
 * Address panel distinguishes the two.
 */
function hasAddress(parts: AddressParts): boolean {
  return Object.values(parts).some((v) => v !== null && v !== '');
}

/**
 * One address block — the picker, then the free-text line, then the postal code.
 *
 * **The picker and the street line run full width; the postal code shares the street's row.**
 * A path reads `Rizal, Antipolo, Bagong Nayon` and a street line carries a lot, a block, a
 * street and a subdivision — both are long values a half-width box would truncate. A postal
 * code is four digits, so it takes the quarter beside the street rather than a row of its own.
 *
 * The same shape as the profile's own address editor, deliberately: the panels mirror the
 * steps, so the form that collects an address and the form that corrects it ask for it
 * identically.
 */
function AddressFields({
  legend,
  idPrefix,
  value,
  onChange,
  saving,
  error,
}: {
  legend: string;
  idPrefix: string;
  value: AddressParts;
  onChange: (next: AddressParts) => void;
  saving: boolean;
  error: ApiError | null;
}) {
  const set = (patch: Partial<AddressParts>) => onChange({ ...value, ...patch });

  // Empty is null on the wire, never '': the column is nullable, and a blank string would read
  // as a part that was answered with nothing.
  const trimmed = (raw: string) => (raw.trim() === '' ? null : raw);

  return (
    <FormFieldset legend={legend}>
      <AddressPicker
        id={`${idPrefix}-place`}
        value={{
          province: value.province,
          cityMunicipality: value.cityMunicipality,
          barangay: value.barangay,
        }}
        onChange={(next) => set(next)}
        error={
          error?.fieldError('province') ??
          error?.fieldError('cityMunicipality') ??
          error?.fieldError('barangay')
        }
        disabled={saving}
      />

      {/*
        The street line and the postal code share a row, split 3:1 — the same shape the
        profile's address editor uses, because the form that collects an address and the form
        that corrects it must ask for it identically.

        NOT equal halves: the street carries a lot, a block, a street and a subdivision, and a
        postal code is four digits.
      */}
      <div className="grid gap-4 sm:grid-cols-[3fr_1fr]">
        <TextField
          id={`${idPrefix}-street`}
          label="Lot No / Street / Bldg"
          placeholder="e.g. Lot 12, Block 3, Mango St., Greenview Subd."
          value={value.streetAddress ?? ''}
          onChange={(e) => set({ streetAddress: trimmed(e.target.value) })}
          error={error?.fieldError('streetAddress')}
          disabled={saving}
        />

        <TextField
          id={`${idPrefix}-zip`}
          label="Postal code"
          value={value.zipCode ?? ''}
          onChange={(e) => set({ zipCode: trimmed(e.target.value) })}
          error={error?.fieldError('zipCode')}
          disabled={saving}
        />
      </div>
    </FormFieldset>
  );
}
